import { LocalDate } from "@js-joda/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomIcon } from "components/icon/customIcon.component";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { Modal } from "components/designV2/modal/modal";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { parseIsoDateToLocalDateOrNull } from "web-apps/company/utils/timeAndDate";
import type { SelectOption } from "../../profilePanel.contract";
import { canApplyMonthlyEffectiveDate } from "../profilePanel.effectiveDate.logic";
import {
  canDeleteHistoryItemForEmploymentStartDate,
  canDeleteHistoryItemWithinLastMonthOrFuture,
  sortHistoryItemsByStartDateDesc,
} from "../profilePanel.history.logic";
import {
  SalaryModalAddAction,
  SalaryModalDeleteButton,
  SalaryModalError,
  SalaryModalFormGrid,
  SalaryModalHistoryCell,
  SalaryModalHistoryHeaderCell,
  SalaryModalHistoryHeaderRow,
  SalaryModalHistoryRow,
  SalaryModalHistorySection,
  SalaryModalHistoryTable,
  SalaryModalHistoryTitle,
  SalaryModalRowActionButton,
  SalaryModalRowActionGroup,
} from "../salary/profilePanel.salaryModal.styled";

interface TaxHistoryItem {
  readonly id: number;
  readonly startDate: string;
  readonly value: number | null;
}

interface ProfilePanelTaxModalProps {
  readonly earliestSelectableDate: LocalDate | null;
  readonly effectiveFromDate: LocalDate | null;
  readonly employmentStartDate: string | null;
  readonly isOpen: boolean;
  readonly isSaving: boolean;
  readonly saveError: string;
  readonly taxHistory: TaxHistoryItem[];
  readonly taxOptions: SelectOption[];
  readonly taxTable: string;
  readonly onAddTax: () => void;
  readonly onChangeFromDate: (date: LocalDate | null) => void;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly onDeleteTax: (taxId: number) => void;
  readonly onTaxTableChange: (taxTable: string) => void;
  readonly onUpdateTaxValue: (taxId: number, value: number) => void;
}

export const ProfilePanelTaxModal = (
  props: ProfilePanelTaxModalProps,
): JSX.Element => {
  const { t } = useTranslation();
  const [editingTaxId, setEditingTaxId] = useState<number | null>(null);
  const [editingTaxValue, setEditingTaxValue] = useState("");

  const sortedHistory = useMemo(() => {
    return sortHistoryItemsByStartDateDesc(props.taxHistory);
  }, [props.taxHistory]);

  const canAddTax = canApplyMonthlyEffectiveDate(
    props.effectiveFromDate,
    props.earliestSelectableDate,
  );

  useEffect(() => {
    if (!props.isOpen) {
      setEditingTaxId(null);
      setEditingTaxValue("");
    }
  }, [props.isOpen]);

  const handleStartEdit = (taxItem: TaxHistoryItem): void => {
    setEditingTaxId(taxItem.id);
    setEditingTaxValue(taxItem.value == null ? "" : String(taxItem.value));
  };

  const handleCancelEdit = (): void => {
    setEditingTaxId(null);
    setEditingTaxValue("");
  };

  const selectedEditOption = props.taxOptions.find(
    (option) => option.value === editingTaxValue,
  );
  const selectedEditValue = Number(selectedEditOption?.value);
  const isEditValueValid =
    selectedEditOption != null && Number.isFinite(selectedEditValue);

  const handleSaveEdit = (): void => {
    if (editingTaxId == null || !isEditValueValid) return;
    props.onUpdateTaxValue(editingTaxId, selectedEditValue);
    handleCancelEdit();
  };

  const saveButton = (
    <PrimaryButton
      label={t("General.Save")}
      isLoading={props.isSaving}
      disabled={props.isSaving || editingTaxId != null}
      onClick={props.onConfirm}
    />
  );

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      label={t("Staff.ProfilePanelChangeTax")}
      size="compact"
      footer={
        <>
          <SecondaryButton
            label={t("General.Cancel")}
            disabled={props.isSaving}
            onClick={props.onClose}
          />
          {editingTaxId != null ? (
            <TooltipHover
              text={t("Staff.ProfilePanelTaxEditNeedsConfirmation")}
              arrow="up"
            >
              <span>{saveButton}</span>
            </TooltipHover>
          ) : (
            saveButton
          )}
        </>
      }
    >
      <SalaryModalFormGrid>
        <Dropdown
          fullWidth
          label={t("Staff.ProfilePanelTaxNewTaxTable")}
          options={props.taxOptions}
          value={props.taxTable}
          onChange={props.onTaxTableChange}
        />
        <DatePicker
          label={t("Staff.ProfilePanelTaxStartDay")}
          value={props.effectiveFromDate}
          onChange={props.onChangeFromDate}
          monthlySelectableDay={1}
          earliestSelectableDate={props.earliestSelectableDate ?? undefined}
        />
        <SalaryModalAddAction>
          <PrimaryButton
            size="small"
            label={t("General.Add")}
            isLoading={props.isSaving}
            disabled={!canAddTax || props.isSaving}
            onClick={props.onAddTax}
          />
        </SalaryModalAddAction>
      </SalaryModalFormGrid>

      <SalaryModalHistorySection>
        <SalaryModalHistoryTitle>
          {t("General.History")}
        </SalaryModalHistoryTitle>
        <SalaryModalHistoryTable>
          <SalaryModalHistoryHeaderRow $columns={3}>
            <SalaryModalHistoryHeaderCell>
              {t("Staff.AddStaffPanelFieldStartDate")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell $isRightAligned>
              {t("Staff.AddStaffPanelFieldTaxTable")}
            </SalaryModalHistoryHeaderCell>
            <SalaryModalHistoryHeaderCell $isCentered>
              {"\u00A0"}
            </SalaryModalHistoryHeaderCell>
          </SalaryModalHistoryHeaderRow>
          {sortedHistory.length === 0 ? (
            <SalaryModalHistoryRow $columns={3}>
              <SalaryModalHistoryCell>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell $isRightAligned>-</SalaryModalHistoryCell>
              <SalaryModalHistoryCell $isCentered>-</SalaryModalHistoryCell>
            </SalaryModalHistoryRow>
          ) : (
            sortedHistory.map((taxItem) => (
              <SalaryModalHistoryRow key={taxItem.id} $columns={3}>
                <SalaryModalHistoryCell>
                  {parseIsoDateToLocalDateOrNull(
                    taxItem.startDate,
                  )?.toString() ?? "-"}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell $isRightAligned>
                  {editingTaxId === taxItem.id ? (
                    <SearchableDropdown
                      options={props.taxOptions}
                      size="compact"
                      value={editingTaxValue}
                      onChange={setEditingTaxValue}
                    />
                  ) : taxItem.value == null ? (
                    "-"
                  ) : (
                    String(taxItem.value)
                  )}
                </SalaryModalHistoryCell>
                <SalaryModalHistoryCell $isCentered>
                  {!canDeleteHistoryItemForEmploymentStartDate(
                    taxItem.startDate,
                    props.employmentStartDate,
                  ) ? (
                    editingTaxId === taxItem.id ? (
                      <SalaryModalRowActionGroup>
                        <SalaryModalRowActionButton
                          type="button"
                          aria-label={t("General.Save")}
                          disabled={props.isSaving || !isEditValueValid}
                          onClick={handleSaveEdit}
                        >
                          <CustomIcon name="v2/checkmark" size="24px" />
                        </SalaryModalRowActionButton>
                        <SalaryModalRowActionButton
                          type="button"
                          aria-label={t("General.Cancel")}
                          disabled={props.isSaving}
                          onClick={handleCancelEdit}
                        >
                          <CustomIcon name="v2/cross" size="24px" />
                        </SalaryModalRowActionButton>
                      </SalaryModalRowActionGroup>
                    ) : (
                      <SalaryModalRowActionButton
                        type="button"
                        aria-label={t("General.Edit")}
                        disabled={props.isSaving}
                        onClick={() => handleStartEdit(taxItem)}
                      >
                        <CustomIcon name="v2/pen" size="20px" />
                      </SalaryModalRowActionButton>
                    )
                  ) : (
                    canDeleteHistoryItemForEmploymentStartDate(
                      taxItem.startDate,
                      props.employmentStartDate,
                    ) &&
                    canDeleteHistoryItemWithinLastMonthOrFuture(
                      taxItem.startDate,
                    ) && (
                      <SalaryModalDeleteButton
                        type="button"
                        aria-label={t("General.Remove")}
                        disabled={props.isSaving}
                        onClick={() => props.onDeleteTax(taxItem.id)}
                      >
                        <CustomIcon name="v2/trash" size="16px" />
                      </SalaryModalDeleteButton>
                    )
                  )}
                </SalaryModalHistoryCell>
              </SalaryModalHistoryRow>
            ))
          )}
        </SalaryModalHistoryTable>
      </SalaryModalHistorySection>

      {props.saveError.length > 0 && (
        <SalaryModalError>{props.saveError}</SalaryModalError>
      )}
    </Modal>
  );
};
